import React, { useState, useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { isEmpty } from 'lodash'

import {
  UserFile,
  useUpdateUserTaskMutation,
  UserTask,
  UserTaskSource,
} from '__generated__/api-types-and-hooks'
import {
  TaskDetailsWrapper,
  DescriptionWrapper,
  DescriptionHeader,
  DescriptionTitle,
} from 'components/AppKanbanBoard/AppTask/AppTask.style'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppTaskFields } from 'components/AppKanbanBoard/AppTask/AppTaskFields/AppTaskFields.component'

import { AppPlayTutorialIcon } from 'components/Common/AppSvgIcons/AppPlayTutorialIcon'
import { AppTaskDescriptionField } from 'components/AppKanbanBoard/AppTask/AppTaskDescriptionField/AppTaskDescriptionField.component'
import { AppTaskPopups } from './AppTaskPopups/AppTaskPopups.component'
import { useUserTask } from './AppTaskHook/AppTaskHook.hook'
import { TaskFormFields } from '../config'
import { AppGenerateTutorial } from './AppTaskFields/AppGenerateTutorial/AppGenerateTutorial.component'
import { AppTaskUserInputFields } from './AppTaskFields/AppTaskUserInputFields/AppTaskUserInputFields.component'
import { useParams } from 'react-router-dom'
import { AppFileUploader } from 'components/AppFileUploader/AppFileUploader.component'
import { AppTaskFileCard } from './AppTaskFileCard/AppTaskFileCard.component'
import { AppComment } from './AppComment/AppComment.component'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'

interface IAppTaskFormProps {
  task?: UserTask
  logo: string
  tenantId: string
  userId: string
  plan?: {
    name: string | null | undefined
    id: string | null | undefined
    planExpectedEndDate: string | null | undefined
  }
  source: UserTaskSource
  setShowTaskModal: React.Dispatch<React.SetStateAction<boolean>>
  setShowTaskSuccessModal?: React.Dispatch<React.SetStateAction<boolean>>
  setIsUpdateTaskSuccess?: React.Dispatch<React.SetStateAction<boolean>>
  setUpdateTaskLoading?: React.Dispatch<React.SetStateAction<boolean>>
  setShowTaskErrorModal?: React.Dispatch<React.SetStateAction<boolean>>
  setCreatedUserTask?: React.Dispatch<React.SetStateAction<UserTask>>
  setFileDeleteSuccess?: React.Dispatch<React.SetStateAction<boolean | null>>
  refetch: () => void
  userName?: string
  advisorsData: Array<{ label: any; value: any }>
  goalId?: string
  goalName?: string
  allTasks?: UserTask[]
  hoursSpentPerDay?: number
  noneEditableFields?: string[]
  userAvatar?: string
  userFirstName?: string
  userLastName?: string
  goalEndDate: string
  assigneeOptions?: IAssigneeOptions[]
  paramUserId?: string
  meetingId?: string
  initialDescription?: string // New prop for pre-populating description
}

export const AppTask: React.FC<IAppTaskFormProps> = ({
  task,
  logo,
  tenantId,
  userId,
  meetingId,
  plan,
  source,
  userName,
  advisorsData,
  assigneeOptions,
  setShowTaskModal,
  setShowTaskSuccessModal,
  setIsUpdateTaskSuccess,
  setShowTaskErrorModal,
  setCreatedUserTask,
  refetch,
  allTasks,
  goalId,
  goalName,
  hoursSpentPerDay = 8,
  noneEditableFields,
  userAvatar,
  userFirstName,
  userLastName,
  goalEndDate,
  paramUserId,
  initialDescription, // Extract the new prop
  setFileDeleteSuccess,
  setUpdateTaskLoading,
}) => {
  const queryClient = useQueryClient()
  const {
    isViewMode,
    tutorialNotFound,
    generatedHowToDo,
    showWarningPopup,
    setShowWarningPopup,
    goalExpectedEndDate,
    setGoalExpectedEndDate,
    planStartDate,
    methods,
    clickedFields,
    isCreateTaskLoading,
    handleClick,
    updateTaskData,
    handleGoalEndDateUpdate,
    handleTaskSubmission,
    handlCreateTask,
    handleStartTutorialGeneration,
    isTutorialGenerating,
    isSearchHowToDoTaskLoading,
    structuredSteps,
    setTutorialNotFound,
    isStartDateBeforePlanStartDate,
    isUpdateTaskSucess,
  } = useUserTask({
    task,
    plan,
    hoursSpentPerDay,
    tenantId,
    userId,
    allTasks,
    goalId,
    meetingId,
    goalName,
    advisorsData,
    source,
    setShowTaskModal,
    setShowTaskSuccessModal,
    setShowTaskErrorModal,
    setCreatedUserTask,
    refetch,
    goalEndDate,
    initialDescription, // Pass the initial description to the hook
    paramUserId,
  })

  useParams()

  const [taskFiles, setTaskFiles] = useState<UserFile[]>(
    [...(task?.files ?? [])].sort(
      (a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime()
    )
  )
  // State for files being added during task creation (before task is saved)
  const [pendingFiles, setPendingFiles] = useState<UserFile[]>([])

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = methods

  const handleUserInputFieldsSubmit = (formData: Record<string, any>) => {
    if (!task) return

    const updatedUserInputFields =
      task.userInputFields?.map((field) => ({
        ...field,
        value: formData[field.name],
      })) ?? []

    setValue('userInputFields', updatedUserInputFields)
    console.log('called update')
    updateTaskData()
    refetch()
    setShowTaskModal && setShowTaskModal(false)
  }

  const {
    mutate: saveTaskFiles,
    isLoading: isFilesSaving,
    isSuccess: isFilesSaved,
  } = useUpdateUserTaskMutation()
  const handleSaveUploadedFiles = async (uploadedFiles: UserFile[] | null) => {
    if (!uploadedFiles) throw new Error('No files uploaded')

    try {
      // Optimistically update the UI
      setTaskFiles((prev) =>
        [...prev, ...uploadedFiles].sort(
          (a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime()
        )
      )
      // Save files to user task
      saveTaskFiles(
        {
          input: {
            tenantId,
            userId: task?.userId ?? '',
            taskId: task?.taskId ?? '',
            files: [...(task?.files ?? []), ...uploadedFiles],
          },
        },
        {
          onSuccess: () => {
            // Invalidate queries to ensure fresh data
            queryClient.invalidateQueries('getUserTasks')
            queryClient.invalidateQueries('getAdvisorAssignedTasks')
            queryClient.invalidateQueries('getBusinessGoal')
            queryClient.invalidateQueries('getUserTask')
          },
        }
      )
    } catch (error) {
      console.error('Upload failed:', error)
      setShowTaskErrorModal && setShowTaskErrorModal(true)
    }
  }

  const handlePendingFilesUpload = async (uploadedFiles: UserFile[] | null) => {
    if (!uploadedFiles) {
      setShowTaskErrorModal && setShowTaskErrorModal(true)
      return
    }
    // Add files to pending state (not yet saved to task)
    setPendingFiles((prev) =>
      [...prev, ...uploadedFiles].sort(
        (a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime()
      )
    )
  }

  useEffect(() => {
    if (isFilesSaved) {
      setShowTaskSuccessModal && setShowTaskSuccessModal(true)
      refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilesSaved])

  // Sync pending files with form for task creation
  useEffect(() => {
    if (pendingFiles.length > 0 && !task) {
      setValue('files', pendingFiles)
    }
  }, [pendingFiles, task, setValue])

  useEffect(() => {
    if (isUpdateTaskSucess && setIsUpdateTaskSuccess) {
      setIsUpdateTaskSuccess(true)
    }
    // else if (!isUpdateTaskSucess && setIsUpdateTaskSuccess) {
    //   setIsUpdateTaskSuccess(false)
    // }
  }, [isUpdateTaskSucess, setIsUpdateTaskSuccess])

  useEffect(() => {
    setTaskFiles(
      [...(task?.files ?? [])].sort(
        (a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime()
      )
    )
  }, [task])

  return (
    <TaskDetailsWrapper>
      <form onSubmit={handleSubmit(handleTaskSubmission)}>
        <AppTaskFields
          meetingId={meetingId}
          userId={userId}
          methods={methods}
          task={task}
          logo={logo}
          clickedFields={clickedFields}
          handleClick={handleClick}
          isViewMode={isViewMode}
          advisorsData={advisorsData}
          userName={userName}
          noneEditableFields={noneEditableFields}
          setValue={setValue}
          setGoalExpectedEndDate={setGoalExpectedEndDate}
          planStartDate={planStartDate}
          goalId={goalId}
          userFirstName={userFirstName}
          userLastName={userLastName}
          userAvatar={userAvatar}
          isStartDateBeforePlanStartDate={isStartDateBeforePlanStartDate}
          assigneeOptions={assigneeOptions}
          paramUserId={paramUserId}
        />
        <hr className="w-full my-10 border-background-hover" />

        {(isViewMode || !task) && (
          <div className="space-y-10">
            <AppFileUploader
              onUploadCompleted={task ? handleSaveUploadedFiles : handlePendingFilesUpload}
              isFilesSaving={isFilesSaving}
              allowedFormats={[]}
            />
            <div className="flex flex-col gap-4">
              {(task ? taskFiles : pendingFiles).map((file) => (
                <AppTaskFileCard
                  onDeleteSuccess={(success: boolean) => {
                    if (success) {
                      task
                        ? setTaskFiles((prev) => prev.filter((f) => f.id !== file.id))
                        : setPendingFiles((prev) => prev.filter((f) => f.id !== file.id))
                    }
                    refetch?.()

                    setFileDeleteSuccess?.(success)
                  }}
                  key={file.id}
                  file={file}
                  isDeletable={true}
                  task={task}
                  refetch={refetch}
                />
              ))}
            </div>
          </div>
        )}

        {isViewMode && <hr className="w-full my-10 border-background-hover" />}

        <DescriptionWrapper>
          <DescriptionHeader>
            <DescriptionTitle>Description</DescriptionTitle>
            {isViewMode && (
              <AppButton
                onClick={() => {
                  // Reset state before starting new tutorial generation
                  setTutorialNotFound(false)
                  handleStartTutorialGeneration()
                }}
                disabled={isTutorialGenerating || isSearchHowToDoTaskLoading}
                size="sm"
                LeftIcon={AppPlayTutorialIcon}
                label="Generate Tutorial"
                variant="primary"
              />
            )}
          </DescriptionHeader>

          <AppTaskDescriptionField
            methods={methods}
            clickedFields={clickedFields}
            handleClick={
              !noneEditableFields?.includes(TaskFormFields.instructionForTask)
                ? handleClick
                : () => handleClick(TaskFormFields.instructionForTask, false)
            }
          />
        </DescriptionWrapper>
        {task && (
          <AppGenerateTutorial
            task={task}
            control={control}
            planName={plan?.name || ''}
            isViewMode={isViewMode}
            tutorialNotFound={tutorialNotFound}
            generatedHowToDo={generatedHowToDo}
            isLoading={isTutorialGenerating || isSearchHowToDoTaskLoading}
            structuredSteps={structuredSteps}
          />
        )}

        {task?.userInputFields && task?.userInputFields.length > 0 && (
          <div className="mt-5">
            <DescriptionHeader>
              <DescriptionTitle>Complete Task</DescriptionTitle>
            </DescriptionHeader>
            <AppTaskUserInputFields
              task={task}
              isViewMode={isViewMode}
              onSubmit={handleUserInputFieldsSubmit}
              scrollToBottom={() => {
                window.scrollTo(0, document.body.scrollHeight)
              }}
            />
          </div>
        )}

        {!isViewMode && (
          <div className="flex justify-end mt-4">
            <AppButton
              label={'Create Play'}
              variant="secondary"
              size="md"
              type="submit"
              disabled={isCreateTaskLoading || !isEmpty(errors) || isStartDateBeforePlanStartDate}
            />
          </div>
        )}
      </form>
      <AppTaskPopups
        showWarningPopup={showWarningPopup}
        setGoalExpectedEndDate={setGoalExpectedEndDate}
        isViewMode={isViewMode}
        task={task}
        setShowWarningPopup={setShowWarningPopup}
        updateTaskData={updateTaskData}
        setValue={setValue}
        handlCreateTask={handlCreateTask}
        handleGoalEndDateUpdate={handleGoalEndDateUpdate}
        goalEndDate={goalExpectedEndDate}
      />

      {task && (
        <AppComment currentUserId={userId} refetchTask={refetch} taskId={task.taskId ?? ''} />
      )}
    </TaskDetailsWrapper>
  )
}
